"use client";

import type { LocaleType } from "@/constants/locales";
import { MapListSyncProvider } from "@/contexts/MapListSyncContext";
import { useMapStore } from "@/hooks/map/useMapStore";
import { trackFirstDiscoverInteraction } from "@/lib/analytics/discoverInteractions";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { defaultAppLocale } from "@/i18n/config";
import { getLocaleDefaultArea } from "@/utils/location";
import { buildDiscoverUrl } from "@/app/[locale]/_lib/urlBuilders";
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type Tab = "events" | "clubs";

type TabsContextType = {
  selectedTab: Tab;
  setTab: (tab: Tab) => void;
};

const TabsContext = createContext<TabsContextType>({
  selectedTab: "events",
  setTab: () => {
    setTimeout(() => {
      throw Error("Attempted to set tab outside of context");
    });
  },
});

export function useTabs(): TabsContextType {
  return use(TabsContext);
}

export function useTabParam(): Tab {
  const params = useSearchParams();
  switch (params.get("tab")) {
    case "clubs":
      return "clubs";
    default:
      return "events";
  }
}

export function SwipeableTabsController({ children }: { children: ReactNode }) {
  const searchParams = useSearchParams();
  const routeParams = useParams<{
    locale?: string;
    location?: string;
    activity?: string;
  }>();
  const router = useRouter();
  const { clearSelection } = useMapStore();
  // Initial tab from query param at mount time
  const initialTab: Tab =
    searchParams.get("tab") === "clubs" ? "clubs" : "events";
  const [selectedTab, setSelectedTab] = useState<Tab>(initialTab);

  const setTabAndState = useCallback(
    (tab: Tab) => {
      // Only a user action reaches setTab; the popstate sync below does not.
      if (tab !== selectedTab) trackFirstDiscoverInteraction("tab_switch");

      const locale = (routeParams.locale || defaultAppLocale) as LocaleType;
      const location = routeParams.location || getLocaleDefaultArea(locale);
      const activity = routeParams.activity;

      // Preserve current query params
      const preserved = new URLSearchParams(searchParams.toString());
      preserved.delete("tab");
      preserved.delete("locale");
      if (tab === "clubs") {
        preserved.set("tab", "clubs");
      }
      const href = buildDiscoverUrl({
        locale,
        locationSlug: location,
        activitySlug: activity,
        query: preserved,
      });

      setSelectedTab(tab);
      clearSelection();
      router.push(href);
    },
    [routeParams, searchParams, router, clearSelection, selectedTab],
  );

  // Sync selectedTab with browser navigation (back/forward)
  useEffect(() => {
    const onPop = () => {
      if (typeof window === "undefined") return;
      const sp = new URLSearchParams(window.location.search);
      const nextTab: Tab = sp.get("tab") === "clubs" ? "clubs" : "events";
      if (nextTab !== selectedTab) {
        clearSelection();
      }
      setSelectedTab(nextTab);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [selectedTab, clearSelection]);

  const contextValue = useMemo(
    () => ({
      selectedTab: selectedTab,
      setTab: setTabAndState,
    }),
    [selectedTab, setTabAndState],
  );

  return (
    <MapListSyncProvider>
      <TabsContext.Provider value={contextValue}>
        {children}
      </TabsContext.Provider>
    </MapListSyncProvider>
  );
}
